import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { DashboardGatewayService } from "./dashboard-gateway.service";
import { PinFilter } from "../models/pin-filter.model";
import {
  MerchantDetail,
  MerchantList,
  PinList,
} from "../models/pin-list.model";
import { MerchantInvite, Pin, PinCheck } from "../models/pin.model";

import {
  Profile,
  TimeZone,
  Country,
  TwoStepVerification,
  ServerMessage,
} from "src/app/shared/models/user.model";
import { CountryList } from "src/app/shared/models/location.model";
import { StateList } from "../models/states.model";
import { CatalogLinks, CatalogLink } from "../models/catalog-link.model";
import { UserProfile } from "src/app/core/models/initials.model";

@Injectable({ providedIn: "root" })
export class DashboardFacadeService {
  constructor(private dashboardGatewayService: DashboardGatewayService) {}

  getPins(filters: PinFilter): Observable<PinList> {
    return this.dashboardGatewayService.getPins(filters);
  }

  getMerchantList(filters: any): Observable<MerchantList> {
    return this.dashboardGatewayService.getMerchantList(filters);
  }

  inviteMerchant(data: MerchantInvite): Observable<ServerMessage> {
    return this.dashboardGatewayService.inviteMerchant(data);
  }

  updateMerchant(id: number, data: MerchantInvite): Observable<MerchantDetail> {
    return this.dashboardGatewayService.updateMerchant(id, data);
  }

  addTerminal(data: MerchantInvite): Observable<MerchantDetail> {
    return this.dashboardGatewayService.createTerminal(data);
  }

  updateTerminal(id: number, data: MerchantInvite): Observable<MerchantDetail> {
    return this.dashboardGatewayService.updateTerminal(id, data);
  }

  resendMerchantInvitation(id: number): Observable<ServerMessage> {
    return this.dashboardGatewayService.resendMerchantInvitation(id);
  }

  connectTerminalToAccount(id: number, data: any): Observable<ServerMessage> {
    return this.dashboardGatewayService.connectTerminalToAccount(id, data);
  }

  userUpdateProfile(data: any, id?): Observable<Profile> {
    return this.dashboardGatewayService.userUpdateProfile(data, id);
  }

  updateUserProfile(data): Observable<UserProfile> {
    return this.dashboardGatewayService.updateUserProfile(data);
  }
  // userUpdateProfileById(data, id?: number) {
  //     return this.dashboardGatewayService.userUpdateProfileById(data, id);
  // }
  verifyUserProfile(data): Observable<UserProfile> {
    return this.dashboardGatewayService.verifyUserProfile(data);
  }
  getUserProfileById(id?: number): Observable<Profile> {
    return this.dashboardGatewayService.getUserProfileById(id);
  }

  getUserProfile(): Observable<UserProfile> {
    return this.dashboardGatewayService.getUserProfile();
  }

  getCompanyProfile(): Observable<Profile> {
    return this.dashboardGatewayService.getCompanyProfile();
  }

  addPin(data: Pin): Observable<Pin> {
    return this.dashboardGatewayService.addPin(data);
  }

  checkPinUsername(data): Observable<PinCheck> {
    return this.dashboardGatewayService.checkPinUsername(data);
  }

  setPinCredentials(id, data): Observable<any> {
    return this.dashboardGatewayService.setPinCredentials(id, data);
  }

  updatePin(id: number, data: Pin): Observable<Pin> {
    return this.dashboardGatewayService.updatePin(id, data);
  }

  updatePinPassword(id: number, data, sub: boolean = false): Observable<any> {
    return this.dashboardGatewayService.updatePinPassword(id, data, sub);
  }

  deletePin(id: number): Observable<Pin> {
    return this.dashboardGatewayService.deletePin(id);
  }

  getTimeZoneList(): Observable<TimeZone[]> {
    return this.dashboardGatewayService.getTimeZoneList();
  }

  // getCountryList(search?: string): Observable<CountryList> {
  //   return this.dashboardGatewayService.getCountryList(search);
  // }

  // getStateList(countryId: any, search?: string): Observable<StateList> {
  //   return this.dashboardGatewayService.getStateList(countryId, search)
  // }

  // getCityList(stateId: any, search?: string) {
  //   return this.dashboardGatewayService.getCityList(stateId, search);
  // }

  changeUserPassword(data): Observable<any> {
    return this.dashboardGatewayService.changeUserPassword(data);
  }

  uploadUserDocument(data) {
    return this.dashboardGatewayService.uploadUserDocument(data);
  }

  uploadCustomerDocument(data) {
    return this.dashboardGatewayService.uploadCustomerDocument(data);
  }

  updateUserDocument(id, data) {
    return this.dashboardGatewayService.updateUserDocument(id, data);
  }

  downloadUserDocument(id) {
    return this.dashboardGatewayService.downloadUserDocument(id);
  }

  getUserDocuments(filters) {
    return this.dashboardGatewayService.getUserDocuments(filters);
  }

  getCustomerDocuments(filters) {
    return this.dashboardGatewayService.getCustomerDocuments(filters);
  }

  downloadCustomerDocument(filters) {
    return this.dashboardGatewayService.downloadCustomerDocument(filters);
  }

  updateCustomerDocuments(id, data) {
    return this.dashboardGatewayService.updateCustomerDocuments(id, data);
  }

  getTwoStepVerification(): Observable<TwoStepVerification> {
    return this.dashboardGatewayService.getTwoStepVerificationCode();
  }

  sendTwoStepVerificationCode(isWhatsapp: boolean = false, subUserCode?) {
    return this.dashboardGatewayService.sendTwoStepVerificationCode(
      isWhatsapp,
      subUserCode
    );
  }

  activateTwoStepVerification(data): Observable<any> {
    return this.dashboardGatewayService.activateTwoStepVerification(data);
  }

  deactivateTwoStepVerification(data, isWhatsapp: boolean): Observable<any> {
    return this.dashboardGatewayService.deactivateTwoStepVerification(
      data,
      isWhatsapp
    );
  }

  activateEmailTwoStepVerification(): Observable<any> {
    return this.dashboardGatewayService.activateEmailTwoStepVerification();
  }

  sendTwoStepVerificationEmail(): Observable<any> {
    return this.dashboardGatewayService.sendTwoStepVerificationEmail();
  }

  disableTwoStepEmail(data): Observable<any> {
    return this.dashboardGatewayService.disableTwoStepEmail(data);
  }

  removeSubUserAuthentication(id: number): Observable<any> {
    return this.dashboardGatewayService.removeSubUserAuthentication(id);
  }
  getCatalogLinks(): Observable<CatalogLinks> {
    return this.dashboardGatewayService.getCatalogLinks();
  }

  addCatalogLink(data: any): Observable<CatalogLink> {
    return this.dashboardGatewayService.addCatalogLinks(data);
  }

  checkCatalogName(Obj: { name: string }): Observable<any> {
    return this.dashboardGatewayService.checkCatalogName(Obj);
  }

  editCatalogLink(id, data: any): Observable<CatalogLink> {
    return this.dashboardGatewayService.editCatalogLink(id, data);
  }

  deleteCatalog(id): Observable<CatalogLink> {
    return this.dashboardGatewayService.deleteCatalog(id);
  }

  getCurrencies(showing: boolean = false): Observable<any> {
    return this.dashboardGatewayService.getCurrencies(showing);
  }

  getWalletBalanceNotifications(): Observable<any> {
    return this.dashboardGatewayService.getWalletBalanceNotifications();
  }

  createWalletBalanceNotification(data): Observable<any> {
    return this.dashboardGatewayService.createWalletBalanceNotification(data);
  }

  updateWalletBalanceNotification(id, data): Observable<any> {
    return this.dashboardGatewayService.updateWalletBalanceNotification(
      id,
      data
    );
  }

  removeWalletBalanceNotification(id): Observable<any> {
    return this.dashboardGatewayService.deleteWalletBalanceNotification(id);
  }

  generatePhoneVerifyCode(is_whatsapp: boolean): Observable<any> {
    return this.dashboardGatewayService.generatePhoneVerifyCode(is_whatsapp);
  }

  verifyPhoneNumber(is_whatsapp: boolean, data): Observable<any> {
    return this.dashboardGatewayService.verifyPhoneNumber(is_whatsapp, data);
  }

  getApiAccessList(filter): Observable<any> {
    return this.dashboardGatewayService.getApiAccessList(filter);
  }

  updateApiAccess(id, data): Observable<any> {
    return this.dashboardGatewayService.updateApiAccess(id, data);
  }

  createApiAccess(data): Observable<any> {
    return this.dashboardGatewayService.createApiAccess(data);
  }

  revokeApiAccess(id: number, data): Observable<any> {
    return this.dashboardGatewayService.revokeApiAccess(id, data);
  }

  generateLoginTwoStepVerification(): Observable<any> {
    return this.dashboardGatewayService.generateLoginTwoStepVerification();
  }

  activateLoginTwoStepVerification(data): Observable<any> {
    return this.dashboardGatewayService.activateLoginTwoStepVerification(data);
  }

  deleteLoginTwoStepVerification(): Observable<any> {
    return this.dashboardGatewayService.deleteLoginTwoStepVerification();
  }

  patchLoginTwoStepVerification(data): Observable<any> {
    return this.dashboardGatewayService.patchLoginTwoStepVerification(data);
  }

  addLoginTwoStepVerificationPhoneNumber(data): Observable<any> {
    return this.dashboardGatewayService.addLoginTwoStepVerificationPhoneNumber(
      data
    );
  }

  confirmTwoStepVerificationPhoneNumber(data): Observable<any> {
    return this.dashboardGatewayService.confirmTwoStepVerificationPhoneNumber(
      data
    );
  }

  removeLoginTwoStepVerification(data): Observable<any> {
    return this.dashboardGatewayService.removeLoginTwoStepVerification(data);
  }

  confirmRemoveLoginTwoStepVerification(data): Observable<any> {
    return this.dashboardGatewayService.confirmRemoveLoginTwoStepVerification(
      data
    );
  }

  getLoginTwoStepVerification(): Observable<any> {
    return this.dashboardGatewayService.getLoginTwoStepVerification();
  }

  toggleEmailLoginTwoStepVerification(): Observable<any> {
    return this.dashboardGatewayService.toggleEmailLoginTwoStepVerification();
  }

  getPromotionsList(data): Observable<any> {
    return this.dashboardGatewayService.getPromotionsList(data);
  }

  getAdminPromotionsList(data): Observable<any> {
    return this.dashboardGatewayService.getAdminPromotionsList(data);
  }

  createPromotion(data): Observable<any> {
    return this.dashboardGatewayService.createPromotion(data);
  }

  updatePromotion(data, id): Observable<any> {
    return this.dashboardGatewayService.updatePromotion(data, id);
  }

  getPromotionsDetail(promotionId): Observable<any> {
    return this.dashboardGatewayService.getPromotionsDetail(promotionId);
  }

  listDailyLimitByAdmin(company):Observable<any>{
    return this.dashboardGatewayService.listDailyLimitByAdmin(company);
  }

  updateDailyLimitByAdmin(id:any,data:any):Observable<any>{
    return this.dashboardGatewayService.updateDailyLimitByAdmin(id,data);
  }

  createDailyLimitByAdmin(data:any){
    return this.dashboardGatewayService.createDailyLimitByAdmin(data);
  }

  resetDailyLimitByAdmin(id:any,data):Observable<any>{
    return this.dashboardGatewayService.resetDailyLimitByAdmin(id,data)
  }


  dailyLimitListPerCompany(user):Observable<any>{
    return this.dashboardGatewayService.dailyLimitListPerCompany(user);
  }


  updateDailyLimitForUser(id,data:any):Observable<any>{
    return this.dashboardGatewayService.updateDailyLimitForUser(id,data)
  }

  createDailyLimitForUser(data:any){
    return this.dashboardGatewayService.createDailyLimitForUser(data);
  }
}
